import React, { useRef, useState } from "react";
import {
  Typography, Box, Stack, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Paper, Divider, Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import InvoicePrint from "../components/InvoicePrint";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import dayjs from "dayjs";
import QRCode from "qrcode";
import { getInvoiceDesignApi, searchFilterApi } from "../api";
import { apiUrl } from "../url";
import { useData } from "../ContextApi/ContextProvider";
import fbrLogo from "../assets/fbr.png";
import {
  DESIGN_CANVAS_WIDTH,
  DESIGN_CANVAS_HEIGHT,
  DEFAULT_BREAKDOWN_ROWS,
  DEFAULT_ITEM_COLUMNS,
  INVOICE_ITEM_FIELD_OPTIONS,
  PRINT_PAGE_HEIGHT_MM,
  getDesignBlock,
  loadLocalInvoiceDesign,
  saveLocalInvoiceDesign,
  toPrintMm,
} from "../utils/invoiceDesign";

const getSavedInvoiceDesign = (globalData) => loadLocalInvoiceDesign(globalData);

const getPrintInvoiceDesign = async (globalData) => {
  try {
    const savedDesign = await getInvoiceDesignApi();
    if (savedDesign?.blocks) return saveLocalInvoiceDesign(savedDesign, globalData);
  } catch {
    // Backend design storage is optional until the API is deployed.
  }

  return getSavedInvoiceDesign(globalData);
};

const getDesignForPrintMode = (design, mode) => {
  if (mode === "normal" || mode === "normal_letterhead") return {};
  if (mode === "saved" || mode === "saved_letterhead") return { ...design, letterhead: "" };
  return design;
};

const isLetterheadPrintMode = (mode) => mode === "normal_letterhead" || mode === "saved_letterhead" || mode === "preprinted";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const toNumber = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const numberToWords = (value) => {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const underThousand = (num) => {
    if (num < 20) return ones[num];
    if (num < 100) return `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${ones[num % 10]}` : ""}`;
    return `${ones[Math.floor(num / 100)]} Hundred${num % 100 ? ` ${underThousand(num % 100)}` : ""}`;
  };
  let remaining = Math.trunc(Math.abs(Number(value) || 0));
  if (!remaining) return "Zero";
  return [
    [1000000000, "Billion"],
    [1000000, "Million"],
    [1000, "Thousand"],
    [1, ""],
  ].reduce((parts, [unit, label]) => {
    const count = Math.floor(remaining / unit);
    if (count) {
      parts.push(`${underThousand(count)}${label ? ` ${label}` : ""}`);
      remaining %= unit;
    }
    return parts;
  }, []).join(" ");
};

const amountToWords = (value) => {
  const amount = Math.abs(Number(value) || 0);
  const rupees = Math.trunc(amount);
  const paisa = Math.round((amount - rupees) * 100);
  return `${numberToWords(rupees)} Rupees${paisa ? ` and ${numberToWords(paisa)} Paisa` : ""}`;
};

const buildModernHistoryInvoicePrintHtml = async (invoice = {}, design = {}, globalData = {}) => {
  const items = Array.isArray(invoice?.items) ? invoice.items : [];
  const formatMoney = (value) => toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const getItemValue = (item, camel, snake) => item?.[camel] ?? item?.[snake] ?? "";
  const totalExcl = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "valueSalesExcludingST", "value_sales_excluding_st")), 0);
  const totalTax = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "salesTaxApplicable", "sales_tax_applicable")), 0);
  const furtherTax = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "furtherTax", "further_tax")), 0);
  const extraTax = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "extraTax", "extra_tax")), 0);
  const fedPayable = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "fedPayable", "fed_payable")), 0);
  const salesTaxWithheldAtSource = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "salesTaxWithheldAtSource", "sales_tax_withheld_at_source")), 0);
  const discount = items.reduce((sum, item) => sum + toNumber(getItemValue(item, "discount", "discount")), 0);
  const getFirstInvoiceValue = (...keys) => {
    for (const key of keys) {
      if (invoice?.[key] !== undefined && invoice?.[key] !== null && invoice?.[key] !== "") return invoice[key];
    }
    return "";
  };
  const sumItemValues = (...keys) => items.reduce((sum, item) => {
    for (const key of keys) {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      const value = item?.[key] ?? item?.[snakeKey];
      if (value !== undefined && value !== null && value !== "") return sum + toNumber(value);
    }
    return sum;
  }, 0);
  const incomeTax236G236H = toNumber(getFirstInvoiceValue(
    "incomeTax236G236H",
    "income_tax_236g_236h",
    "incomeTax236G",
    "income_tax_236g",
    "incomeTax236H",
    "income_tax_236h",
    "tax236G236H",
    "tax_236g_236h"
  )) || sumItemValues("incomeTax236G236H", "incomeTax236G", "incomeTax236H", "tax236G236H");
  const incomeTaxAdd = toNumber(getFirstInvoiceValue(
    "incomeTaxAdv",
    "income_tax_adv",
    "incomeTaxAdvance",
    "income_tax_advance",
    "advanceIncomeTax",
    "advance_income_tax",
    "incomeTaxAdd",
    "income_tax_add"
  )) || sumItemValues("incomeTaxAdv", "incomeTaxAdvance", "advanceIncomeTax", "incomeTaxAdd");
  const salesBeforeDiscount = totalExcl + discount;
  const totalIncl = totalExcl + totalTax + furtherTax - discount;
  const isDesignBlockVisible = (blockId) => getDesignBlock(design, blockId)?.visible !== false;
  const companyLogo = design?.logo || "";
  const showCompanyLogo = isDesignBlockVisible("company_logo");
  const showSellerContact = isDesignBlockVisible("seller_contact");
  const showInvoiceNote = isDesignBlockVisible("invoice_note");
  const showAdditionalInfo = isDesignBlockVisible("additional_info");
  const logoBlock = getDesignBlock(design, "company_logo") || {};
  const logoWidth = Number(logoBlock.w) || 132;
  const logoHeight = Number(logoBlock.h) || 98;
  const logoCenterX = (Number(logoBlock.x) || 0) + (logoWidth / 2);
  const logoTopOffset = Math.round((Number(logoBlock.y ?? 34) - 34) * 1.3);
  const logoSlotStyle = `top:${logoTopOffset}px;left:${((logoCenterX * 100) / DESIGN_CANVAS_WIDTH).toFixed(2)}%;width:${((logoWidth * 100) / DESIGN_CANVAS_WIDTH).toFixed(2)}%;height:${Math.round(logoHeight * 1.06)}px;`;
  const sellerBusinessName = invoice?.sellerBusinessName || invoice?.seller_business_name || globalData?.company_name || globalData?.full_name || "";
  const sellerNTNCNIC = invoice?.sellerNTNCNIC || invoice?.seller_ntn_cnic || globalData?.ntn_cnic || "";
  const sellerAddress = invoice?.sellerAddress || invoice?.seller_address || globalData?.business_address || globalData?.city || "";
  const sellerProvince = invoice?.sellerProvince || invoice?.seller_province || globalData?.province || "";
  const sellerEmail = design?.sellerContactEmail || invoice?.sellerEmail || invoice?.seller_email || globalData?.email || "";
  const sellerPhone = design?.sellerContactPhone || invoice?.sellerPhone || invoice?.seller_phone || globalData?.phone || globalData?.mobile || "";
  const buyerSalesTaxNo = invoice?.buyerSalesTaxNo || invoice?.buyer_sales_tax_no || "";
  const buyerBusinessName = invoice?.buyerBusinessName || invoice?.buyer_business_name || "";
  const buyerNTNCNIC = invoice?.buyerNTNCNIC || invoice?.buyer_ntn_cnic || buyerSalesTaxNo || "";
  const buyerAddress = invoice?.buyerAddress || invoice?.buyer_address || "";
  const buyerProvince = invoice?.buyerProvince || invoice?.buyer_province || "";
  const invoiceNumber = invoice?.invoiceNumber || invoice?.invoice_number || "";
  const invoiceDate = invoice?.invoiceDate || invoice?.invoice_date || "";
  const invoiceType = invoice?.invoiceType || invoice?.invoice_type || "Sale Invoice";
  const fbrInvoiceNo = invoice?.fbrinvoicenumber || invoice?.fbr_invoice_number || "";
  const qrValue = fbrInvoiceNo;
  const qrDataUrl = qrValue
    ? await QRCode.toDataURL(String(qrValue), { width: 114, margin: 0, errorCorrectionLevel: "H" }).catch(() => "")
    : "";
  const formatInvoiceDate = (value) => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY") : value || "";
  };
  const formatTaxPeriod = (value, fallbackDate) => {
    const raw = String(value || "").trim();
    const yearMonth = raw.match(/^(\d{4})[-/](\d{1,2})$/);
    if (yearMonth) return `${yearMonth[2].padStart(2, "0")}/${yearMonth[1]}`;
    const monthYear = raw.match(/^(\d{1,2})[-/](\d{4})$/);
    if (monthYear) return `${monthYear[1].padStart(2, "0")}/${monthYear[2]}`;
    if (raw) return raw;
    const parsed = dayjs(fallbackDate);
    return parsed.isValid() ? parsed.format("MM/YYYY") : "";
  };
  const getItemColumnLabel = (key) => INVOICE_ITEM_FIELD_OPTIONS.find((field) => field.key === key)?.label || key;
  const getItemColumnValue = (item, key, index) => {
    const quantity = toNumber(getItemValue(item, "quantity", "quantity"));
    const valueExcl = toNumber(getItemValue(item, "valueSalesExcludingST", "value_sales_excluding_st"));
    const tax = toNumber(getItemValue(item, "salesTaxApplicable", "sales_tax_applicable"));
    const itemFurtherTax = toNumber(getItemValue(item, "furtherTax", "further_tax"));
    const itemDiscount = toNumber(getItemValue(item, "discount", "discount"));
    const moneyFields = new Set([
      "unitPrice",
      "lineTotal",
      "totalValues",
      "valueSalesExcludingST",
      "fixedNotifiedValueOrRetailPrice",
      "salesTaxApplicable",
      "salesTaxWithheldAtSource",
      "extraTax",
      "furtherTax",
      "fedPayable",
      "discount",
      "totalValueIncludingSalesTax",
    ]);

    if (key === "sr") return index + 1;
    if (key === "unitPrice") return formatMoney(quantity ? valueExcl / quantity : toNumber(getItemValue(item, "fixedNotifiedValueOrRetailPrice", "fixed_notified_value_or_retail_price") || getItemValue(item, "totalValues", "total_values")));
    if (key === "lineTotal") return formatMoney(valueExcl + itemDiscount);
    if (key === "totalValueIncludingSalesTax") return formatMoney(valueExcl + tax + itemFurtherTax - itemDiscount);
    if (key === "quantity") return quantity ? quantity.toLocaleString(undefined, { maximumFractionDigits: 4 }) : getItemValue(item, "quantity", "quantity");
    if (moneyFields.has(key)) return formatMoney(getItemValue(item, key, key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)));
    return getItemValue(item, key, key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`));
  };
  const configuredColumns = Array.isArray(design?.itemColumns) && design.itemColumns.length
    ? design.itemColumns
    : DEFAULT_ITEM_COLUMNS.map((key) => ({ key, visible: true }));
  const visibleItemColumns = [
    ...configuredColumns.filter((column) => column.visible !== false),
    ...((Array.isArray(design?.customItemColumns) ? design.customItemColumns : []).filter((column) => column.visible !== false)),
  ];
  const breakdownValues = {
    salesBeforeDiscount,
    totalExcl,
    discount,
    totalTax,
    furtherTax,
    extraTax,
    fedPayable,
    salesTaxWithheldAtSource,
    incomeTax236G236H,
    incomeTaxAdd,
    grossInvoiceValue: totalIncl,
    netPayableAmount: totalIncl,
  };
  const configuredBreakdownRows = Array.isArray(design?.breakdownRows) && design.breakdownRows.length ? design.breakdownRows : DEFAULT_BREAKDOWN_ROWS;
  const visibleBreakdownRows = configuredBreakdownRows.filter((row) => row.visible !== false && row.valueType !== "words");
  const showTotalBreakdown = visibleBreakdownRows.length > 0;
  const showAmountWords = configuredBreakdownRows.find((row) => row.id === "amountWords")?.visible !== false;
  const additionalInfoRows = [
    { label: "Terms of Sale:", value: invoice?.termsOfSale || invoice?.terms_of_sale || "" },
    { label: "Invoice Ref No.:", value: invoice?.invoiceRefNo || invoice?.invoice_ref_no || "" },
    { label: "Buyer Registration Type:", value: invoice?.buyerRegistrationType || invoice?.buyer_registration_type || "" },
    { label: "Scenario ID:", value: invoice?.scenarioId || invoice?.scenario_id || "" },
  ];
  const headerCells = visibleItemColumns.map((column) => `<th>${escapeHtml(column.label || getItemColumnLabel(column.key))}</th>`).join("");
  const rows = items.map((item, index) => `
    <tr>
      ${visibleItemColumns.map((column) => `<td>${escapeHtml(column.id ? column.value : getItemColumnValue(item, column.key, index))}</td>`).join("")}
    </tr>
  `).join("");
  const breakdownRowsHtml = visibleBreakdownRows.map((row) => {
    const value = row.valueType === "custom" ? row.value : formatMoney(breakdownValues[row.id]);
    return `<tr class="${row.isNet ? "modern-net" : ""}"><td>${escapeHtml(row.label)}</td><td>${escapeHtml(value || "-")}</td></tr>`;
  }).join("");

  return `
    <style>
      html, body { margin: 0; padding: 0; }
      .history-invoice-modern { width: 794px; min-height: 1123px; padding: 48px 34px 40px; background:#fff; color:#111; font-family:Arial,Helvetica,sans-serif; font-size:14px; box-sizing:border-box; }
      .modern-header { position:relative; display:grid; grid-template-columns:1fr 170px 1fr; align-items:start; min-height:130px; }
      .modern-di { display:flex; align-items:center; gap:5px; color:#7c8795; font-size:20px; font-weight:700; line-height:1; }
      .modern-di-mark { width:33px; height:33px; border-left:7px solid #5bb970; border-bottom:7px solid #5bb970; border-top:7px solid #3266ad; border-right:7px solid #3266ad; border-radius:0 17px 17px 0; box-sizing:border-box; }
      .modern-seller-brand { margin-top:14px; line-height:1.27; }
      .modern-seller-brand b { display:block; font-size:16px; margin-bottom:7px; }
      .modern-company-logo { width:100%; height:100%; object-fit:contain; margin:0 auto; display:block; transform:scale(1.14); }
      .modern-logo-slot { position:absolute; top:0; left:50%; width:190px; height:104px; transform:translateX(-50%); text-align:center; overflow:hidden; }
      .modern-fbr { display:flex; justify-content:flex-end; align-items:flex-start; gap:24px; }
      .modern-fbr-stack { text-align:center; padding-top:8px; }
      .modern-fbr-stack img { width:86px; height:70px; object-fit:contain; }
      .modern-qr { width:82px; height:82px; object-fit:contain; image-rendering:pixelated; }
      .modern-qr-fallback { width:82px; height:82px; border:1px solid #111; box-sizing:border-box; }
      .modern-header-right { grid-column:3; justify-self:end; }
      .modern-title { text-align:center; font-size:20px; font-weight:800; margin:0 0 34px; }
      .modern-info { display:grid; grid-template-columns:1.05fr 1.05fr .95fr; gap:42px; margin-bottom:22px; }
      .modern-info h2 { font-size:16px; margin:0 0 18px; font-weight:800; }
      .modern-kv { display:grid; grid-template-columns:95px 1fr; gap:4px 13px; line-height:1.23; }
      .modern-kv b { font-weight:800; }
      .modern-section-heading { font-size:16px; font-weight:800; margin:0 0 16px; }
      .modern-table { width:100%; border-collapse:collapse; table-layout:fixed; margin:0 0 34px; page-break-inside:auto; }
      .modern-table th, .modern-table td { border:1px solid #c9c9c9; padding:10px 8px; vertical-align:middle; }
      .modern-table th { background:#f7f7f7; font-size:8px; font-weight:800; text-align:center; }
      .modern-table td { font-size:8px; text-align:right; }
      .modern-table td:nth-child(3) { text-align:left; }
      .modern-table thead { display:table-header-group; }
      .modern-table tr { page-break-inside:avoid; break-inside:avoid; }
      .modern-lower { display:grid; grid-template-columns:1.23fr 1fr; gap:20px; align-items:start; page-break-inside:avoid; break-inside:avoid; }
      .modern-panel { border:1px solid #c9c9c9; margin-bottom:20px; }
      .modern-panel-title { background:#f7f7f7; border-bottom:1px solid #c9c9c9; padding:10px 9px; font-weight:800; font-size:12px; }
      .modern-note { min-height:66px; padding:10px 9px; line-height:1.45; font-size:12px; }
      .modern-custom-row { display:grid; grid-template-columns:160px 1fr; min-height:24px; border-top:1px solid #d8d8d8; align-items:center; }
      .modern-custom-row b, .modern-custom-row span { padding:5px 9px; font-size:12px; }
      .modern-custom-row b { color:#374151; }
      .modern-total-table { width:100%; border-collapse:collapse; table-layout:fixed; }
      .modern-total-table th, .modern-total-table td { border:1px solid #c9c9c9; padding:11px 9px; }
      .modern-total-table th { background:#f7f7f7; text-align:left; font-size:12px; font-weight:800; }
      .modern-total-table td { text-align:right; font-size:12px; }
      .modern-total-table td:first-child { text-align:left; }
      .modern-net td { background:#f7f7f7; font-weight:800; }
      .modern-words { border:1px solid #c9c9c9; border-top:0; padding:9px; font-size:11px; line-height:1.1; }
      .modern-words-standalone { border-top:1px solid #c9c9c9; }
      .modern-page-footer { display:none; }
      @media print {
        @page { size:A4 portrait; margin:0; @bottom-right { content:"Page " counter(page) " of " counter(pages); font-family:Arial,Helvetica,sans-serif; font-size:9px; color:#6b7280; } }
        .history-invoice-modern { width:210mm; min-height:297mm; padding:13mm 9mm 16mm; }
        .modern-lower { break-inside:avoid; page-break-inside:avoid; }
        .modern-page-footer { display:block; position:fixed; right:9mm; bottom:6mm; color:#6b7280; font-size:9px; font-family:Arial,Helvetica,sans-serif; }
        .modern-page-footer::after { content:"Page " counter(page) " of " counter(pages); }
      }
    </style>
    <div class="history-invoice-modern">
      <div class="modern-header">
        <div>
          <div class="modern-di"><span class="modern-di-mark"></span><span>DIGITAL<br />INVOICING</span></div>
          <div class="modern-seller-brand"><b>${escapeHtml(sellerBusinessName)}</b>${showSellerContact && sellerEmail ? `<br />${escapeHtml(sellerEmail)}` : ""}${showSellerContact && sellerPhone ? `<br />${escapeHtml(sellerPhone)}` : ""}</div>
        </div>
        <div class="modern-logo-slot" style="${logoSlotStyle}">${showCompanyLogo && companyLogo ? `<img class="modern-company-logo" src="${companyLogo}" alt="Company logo" />` : ""}</div>
        <div class="modern-header-right"><div class="modern-fbr"><div class="modern-fbr-stack"><img src="${fbrLogo}" alt="FBR Digital Invoicing" /></div>${qrDataUrl ? `<img class="modern-qr" src="${qrDataUrl}" alt="FBR QR Code" />` : `<div class="modern-qr-fallback"></div>`}</div></div>
      </div>
      <h1 class="modern-title">Sales Tax Invoice</h1>
      <div class="modern-info">
        <div><h2>Seller Information</h2><div class="modern-kv"><b>Business Name:</b><span>${escapeHtml(sellerBusinessName)}</span><b>Registration No.:</b><span>${escapeHtml(sellerNTNCNIC)}</span><b>STRN No.:</b><span>${escapeHtml(invoice?.sellerSTRN || invoice?.seller_strn || globalData?.strn || "")}</span><b>Address:</b><span>${escapeHtml(sellerAddress)}</span><b>Province:</b><span>${escapeHtml(sellerProvince)}</span></div></div>
        <div><h2>Buyer Information</h2><div class="modern-kv"><b>Business Name:</b><span>${escapeHtml(buyerBusinessName)}</span><b>Registration No.:</b><span>${escapeHtml(buyerNTNCNIC)}</span><b>STRN No.:</b><span>${escapeHtml(invoice?.buyerSTRN || invoice?.buyer_strn || invoice?.buyerSalesTaxNo || invoice?.buyer_sales_tax_no || "")}</span><b>Address:</b><span>${escapeHtml(buyerAddress)}</span><b>Province:</b><span>${escapeHtml(buyerProvince)}</span></div></div>
        <div><h2>Invoice Summary</h2><div class="modern-kv"><b>FBR Invoice No.:</b><span>${escapeHtml(fbrInvoiceNo || "N/A")}</span><b>Seller Invoice No.:</b><span>${escapeHtml(invoiceNumber)}</span><b>Invoice Date:</b><span>${escapeHtml(formatInvoiceDate(invoiceDate))}</span><b>Invoice Type:</b><span>${escapeHtml(invoiceType)}</span><b>Tax Period:</b><span>${escapeHtml(formatTaxPeriod(invoice?.taxPeriod || invoice?.tax_period, invoiceDate))}</span></div></div>
      </div>
      <div class="modern-section-heading">Item Details</div>
      <table class="modern-table"><colgroup>${visibleItemColumns.map(() => `<col style="width:${(100 / Math.max(visibleItemColumns.length, 1)).toFixed(2)}%;" />`).join("")}</colgroup><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table>
      <div class="modern-lower">
        <div>
          ${showInvoiceNote ? `<div class="modern-panel"><div class="modern-panel-title">INVOICE NOTE</div><div class="modern-note">${escapeHtml(invoice?.remarks || invoice?.note || "")}</div></div>` : ""}
          ${showAdditionalInfo ? `<div class="modern-panel"><div class="modern-panel-title">ADDITIONAL INFORMATION</div>${additionalInfoRows.map((row) => `<div class="modern-custom-row"><b>${escapeHtml(row.label)}</b><span>${escapeHtml(row.value || "-")}</span></div>`).join("")}</div>` : ""}
        </div>
        <div>
          ${showTotalBreakdown ? `<table class="modern-total-table"><tbody><tr><th>Total Breakdown</th><th>Value (PKR)</th></tr>${breakdownRowsHtml}</tbody></table>` : ""}
          ${showAmountWords ? `<div class="modern-words ${showTotalBreakdown ? "" : "modern-words-standalone"}"><b>Net Payable Amount (In Words):</b>${escapeHtml(amountToWords(totalIncl))}</div>` : ""}
        </div>
      </div>
      <div class="modern-page-footer"></div>
    </div>
  `;
};

const blockCss = (design, id, selector) => {
  const block = getDesignBlock(design, id);
  if (!block) return "";
  if (block.visible === false) return `#printable-content .page ${selector}{display:none!important;}`;
  const heightRule = id === "items_table"
    ? "height:auto!important;max-height:none!important;"
    : `height:${toPrintMm(block.h, "y")}!important;`;

  return `
    #printable-content .page ${selector}{
      position:absolute!important;
      left:${toPrintMm(block.x, "x")}!important;
      top:${toPrintMm(block.y, "y")}!important;
      width:${toPrintMm(block.w, "x")}!important;
      min-width:0!important;
      ${heightRule}
      overflow:hidden!important;
      margin:0!important;
      box-sizing:border-box!important;
    }
  `;
};

const buildDesignPrintCss = (design) => {
  if (!Array.isArray(design?.blocks)) return "";

  return `
    <style>
      #printable-content .page{
        position:relative!important;
        min-height:${PRINT_PAGE_HEIGHT_MM}mm!important;
      }
      #printable-content .konnect-letterhead-bg{
        position:absolute!important;
        inset:0!important;
        width:100%!important;
        height:100%!important;
        object-fit:cover!important;
        z-index:0!important;
        pointer-events:none!important;
      }
      #printable-content .page > :not(.konnect-letterhead-bg){
        position:relative;
        z-index:1;
      }
      #printable-content .page .cols{
        display:block!important;
        position:static!important;
        margin:0!important;
      }
      #printable-content .page .brandRow{
        display:block!important;
        margin:0!important;
      }
      #printable-content .page > table th,
      #printable-content .page > table td{
        padding:0.9mm 1.1mm!important;
        line-height:1.05!important;
      }
      #printable-content .page .sign .sigL > div{
        display:flex!important;
        align-items:baseline!important;
        gap:1.5mm!important;
        white-space:nowrap!important;
      }
      #printable-content .page .sign .sigL .line{
        flex:1!important;
        min-width:0!important;
      }
      ${blockCss(design, "invoice_title", "> .ribbon")}
      ${blockCss(design, "invoice_meta", "> .metaRow")}
      ${blockCss(design, "seller_info", "> .cols > .block:first-child")}
      ${blockCss(design, "buyer_info", "> .cols > .block:nth-child(2)")}
      ${blockCss(design, "description_code", "> .right")}
      ${blockCss(design, "items_table", "> table")}
      ${blockCss(design, "fbr_invoice", "> .note")}
      ${blockCss(design, "verification", "> .brandRow > .brandLeft")}
      ${blockCss(design, "totals", "> .brandRow > .totalsCol")}
      ${blockCss(design, "signature", "> .sign")}
      #printable-content .konnect-custom-logo{
        position:absolute!important;
        z-index:3!important;
        margin:0!important;
        box-sizing:border-box!important;
      }
      #printable-content .konnect-custom-logo img{
        width:100%!important;
        height:100%!important;
        object-fit:contain!important;
        display:block!important;
      }
    </style>
  `;
};

const injectLetterheadBackground = (html, design) => {
  if (!design?.letterhead) return html;

  const template = document.createElement("template");
  template.innerHTML = html;
  const page = template.content.querySelector(".page");
  if (!page) return html;

  const background = document.createElement("img");
  background.className = "konnect-letterhead-bg";
  background.alt = "Letterhead";
  background.src = design.letterhead;
  page.prepend(background);

  return template.innerHTML;
};

const buildDesignLogoHtml = (design) => {
  const logoBlock = getDesignBlock(design, "company_logo") || getDesignBlock(design, "logo");
  if (!design?.logo || logoBlock?.visible === false) return "";

  return `
    <div
      class="konnect-custom-logo"
      style="left:${toPrintMm(logoBlock?.x ?? 42, "x")};top:${toPrintMm(logoBlock?.y ?? 38, "y")};width:${toPrintMm(logoBlock?.w ?? 74, "x")};height:${toPrintMm(logoBlock?.h ?? 36, "y")};"
    >
      <img src="${design.logo}" alt="Company logo" />
    </div>
  `;
};

const applyAutoFlowPrintLayout = (container, design) => {
  if (!container || !Array.isArray(design?.blocks)) return;

  const page = container.querySelector(".page");
  const table = page?.querySelector(":scope > table");
  const tableBlock = getDesignBlock(design, "items_table");
  if (!page || !table || !tableBlock) return;

  const pageRect = page.getBoundingClientRect();
  const yScale = pageRect.height / DESIGN_CANVAS_HEIGHT;
  const tableRect = table.getBoundingClientRect();
  const tableBottom = table.offsetTop + tableRect.height;
  const designedTableBottom = (Number(tableBlock.y || 0) + Number(tableBlock.h || 0)) * yScale;

  const fbrBlock = getDesignBlock(design, "fbr_invoice");
  const fbrTop = fbrBlock ? Number(fbrBlock.y || 0) * yScale : tableBottom;
  const shift = Math.max(0, tableBottom + (8 * yScale) - fbrTop, tableBottom - designedTableBottom);
  if (!shift) return;

  const lowerBlocks = [
    ["fbr_invoice", ":scope > .note"],
    ["verification", ":scope > .brandRow > .brandLeft"],
    ["totals", ":scope > .brandRow > .totalsCol"],
    ["signature", ":scope > .sign"],
  ];

  let contentBottom = tableBottom;
  lowerBlocks.forEach(([id, selector]) => {
    const block = getDesignBlock(design, id);
    const element = page.querySelector(selector);
    if (!block || !element || block.visible === false) return;

    const nextTop = (Number(block.y || 0) * yScale) + shift;
    element.style.top = `${nextTop}px`;
    element.style.position = "absolute";
    contentBottom = Math.max(contentBottom, nextTop + element.getBoundingClientRect().height);
  });

  page.style.minHeight = `${Math.max(pageRect.height, contentBottom + (24 * yScale))}px`;
};



const History = () => {
  const [searchMode, setSearchMode] = useState("date"); // 'date' | 'company' | 'fbr'
  const [from, setFrom] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [searchTerm, setSearchTerm] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [printChoiceInvoiceId, setPrintChoiceInvoiceId] = useState(null);
  const [printMode, setPrintMode] = useState("normal");
  const componentRef = useRef();
  const handlePrints = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Sales-Tax-Invoice",
    // This is a key part of the solution. We add styles specifically for printing.
    pageStyle: `
      @media print {
        body * {
          visibility: hidden;
        }
        #printable-content, #printable-content * {
          visibility: visible;
        }
        #printable-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        #printable-content.print-letterhead {
          padding-top: var(--letterhead-top, 165px);
          box-sizing: border-box;
        }
      }
    `,
  });
  const preparePrintHtml = (html) => {
    const template = document.createElement("template");
    template.innerHTML = html;
    template.content.querySelectorAll("*").forEach((element) => {
      element.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = node.textContent.replace(/GST No\./gi, "Sales Tax No.");
        }
      });
    });
    const taxNumberPattern = /^(GST No\.|Sales Tax No\.)\s*:?\s*(.*)$/i;
    const labelPattern = /^(GST No\.|Sales Tax No\.|National Tax No\.|Terms of Sale|Terms of Sales|Address|Supplier's Name|Buyer's Name)\s*:?/i;
    const hasValue = (value) => {
      const normalizedValue = value.replace(/\u00a0/g, " ").trim();
      return normalizedValue && !/^(null|undefined)$/i.test(normalizedValue) && !labelPattern.test(normalizedValue);
    };

    template.content.querySelectorAll("*").forEach((element) => {
      const text = element.textContent.replace(/\u00a0/g, " ").trim();
      const match = text.match(taxNumberPattern);

      if (!match || hasValue(match[2])) return;

      const nextText = element.nextSibling?.textContent?.replace(/\u00a0/g, " ").trim() || "";
      const nextElementText = element.nextElementSibling?.textContent?.replace(/\u00a0/g, " ").trim() || "";

      if (!hasValue(nextText) && !hasValue(nextElementText)) {
        element.remove();
      }
    });

    template.content.querySelectorAll("img").forEach((image) => {
      const src = image.getAttribute("src")?.trim();

      if (!src || image.getAttribute("alt")?.toLowerCase() === "logo") {
        image.setAttribute("src", fbrLogo);
        return;
      }

      if (!/^(https?:|data:|blob:)/i.test(src)) {
        image.setAttribute("src", new URL(src, "https://konnectfbr.com").href);
      }
    });

    return template.innerHTML;
  };
  const handleFetchInvoice = async (val, mode = "normal") => {
    try {
      setPrintMode(mode);
      const savedDesign = await getPrintInvoiceDesign(globalData);

      if (mode === "saved" || mode === "saved_letterhead") {
        const invoice = searchInvoices.find((item) => String(item?.id) === String(val));
        if (!invoice) return;

        setHtmlContent(await buildModernHistoryInvoicePrintHtml(invoice, savedDesign, globalData));
        setPrintChoiceInvoiceId(null);

        setTimeout(() => {
          handlePrints();
        }, 300);
        return;
      }

      const token = localStorage.getItem("jwt_token"); 

      const response = await fetch(`${apiUrl}/print_invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token:token
        },
        body: JSON.stringify({ invoice_id: val }), 
      });
  
      const data = await response.text(); 
      const design = getDesignForPrintMode(savedDesign, mode);
      const preparedHtml = injectLetterheadBackground(preparePrintHtml(data), design);
      setHtmlContent(`${buildDesignPrintCss(design)}${buildDesignLogoHtml(design)}${preparedHtml}`);
      setPrintChoiceInvoiceId(null);

      setTimeout(() => {
        applyAutoFlowPrintLayout(componentRef.current, design);
        handlePrints();
      }, 300);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  
   
  };

  const handleOpenPrintChoice = (invoiceId) => {
    setPrintChoiceInvoiceId(invoiceId);
  };
  
  
  // const [filtered, setFiltered] = useState(apiInvoices);
  const [viewInvoice, setViewInvoice] = useState(null);
const [searchInvoices,setSearchInvoices]=useState([])
  // Print setup
  const [printInvoice, setPrintInvoice] = useState(null);
  const printRef = useRef();
  const {setLoader, globalData}=useData()
  const historyStartDate = dayjs()
    .subtract(globalData?.is_invoice_history ? 5 : 1, globalData?.is_invoice_history ? "year" : "month")
    .format("YYYY-MM-DD");
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printInvoice ? `Invoice_${printInvoice.invoice_number}` : "Invoice",
    removeAfterPrint: true,
  });
  const handleSearchClick = async() => {
    if (searchMode === "date") {
      let payload={to:to,
        from:from
      }
      setSearchInvoices([]);

      try {
        const res = await searchFilterApi(payload,setLoader,'/InvoicesByDate');
        if (res.message === "Success") {
          setSearchInvoices(res?.response?.result);
        }
      } catch (error) {} 

    } else if (searchMode === "company") {
      let payload={invoice_type:"2",
        invoice_number:searchTerm
      }
      setSearchInvoices([]);

      try {
        const res = await searchFilterApi(payload,setLoader,'/searchInvoice');
        if (res.message === "Success") {
          setSearchInvoices(res?.response?.result);
        }
      } catch (error) {} 
    } else if (searchMode === "fbr") {
      let payload={invoice_type:"3",
        invoice_number:searchTerm
      }
      setSearchInvoices([]);

      try {
        const res = await searchFilterApi(payload,setLoader,'/searchInvoice');
        if (res.message === "Success") {
          setSearchInvoices(res?.response?.result);
        }
      } catch (error) {} 
    }
  };
  
  const handleFilter = async() => {
   };

  const handlePrintClick = (invoice) => {
    setPrintInvoice(invoice);
    setTimeout(() => {
      handlePrint();
      setPrintInvoice(null);
    }, 150);
  };

  return (
    <Box
      minHeight="100vh"
      sx={{
        background: "transparent",
        pb: 5,
        maxWidth: 1560,
        mx: "auto",
      }}
    >
      {/* Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          border: "1px solid rgba(37, 99, 235, 0.14)",
          background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 56%, #ecfdf5 100%)",
          boxShadow: "0 16px 36px rgba(15, 23, 42, 0.07)",
        }}
      >
  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
    <Box sx={{ mr: { xs: 0, md: 1.5 }, minWidth: 230, flex: "1 1 280px" }}>
      <Typography sx={{ color: "#2563eb", fontSize: 12, fontWeight: 950, textTransform: "uppercase" }}>
        Search archive
      </Typography>
      <Typography sx={{ fontWeight: 950, color: "#0f172a", fontSize: { xs: 22, md: 26 }, lineHeight: 1.15, mt: 0.4 }}>
        Invoice History
      </Typography>
      <Typography sx={{ color: "#64748b", fontSize: 14, mt: 0.5 }}>
        Search submitted invoices, review details, and print official copies.
      </Typography>
    </Box>
    <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 210 } }}>
      <InputLabel>Search By</InputLabel>
      <Select
        value={searchMode}
        label="Search By"
        sx={{ bgcolor: "#fff", borderRadius: 1.5, boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)" }}
        onChange={(e) => setSearchMode(e.target.value)}
      >
        <MenuItem value="date">Date Range</MenuItem>
        <MenuItem value="company">Company Invoice Number</MenuItem>
        <MenuItem value="fbr">FBR Invoice Number</MenuItem>
      </Select>
    </FormControl>

    {searchMode === "date" && (
      <>
        <TextField
          type="date"
          label="From"
          value={from}
          inputProps={{ min: historyStartDate, max: to }}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: { xs: "100%", md: 160 }, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff", boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)" } }}
        />
        <TextField
          type="date"
          label="To"
          value={to}
          inputProps={{ min: historyStartDate, max: dayjs().format("YYYY-MM-DD") }}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: { xs: "100%", md: 160 }, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff", boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)" } }}
        />
        <Button
          variant="contained"
          onClick={handleSearchClick} // <-- alert function
          startIcon={<SearchIcon />}
          sx={{
            bgcolor: "#0f172a",
            fontWeight: 900,
            borderRadius: 1.5,
            px: 2.25,
            minHeight: 42,
            boxShadow: "0 12px 24px rgba(15, 23, 42, 0.18)",
            textTransform: "none",
            "&:hover": { bgcolor: "#1e293b", boxShadow: "0 12px 24px rgba(15, 23, 42, 0.18)" }
          }}
        >
          Filter
        </Button>
      </>
    )}

    {(searchMode === "company" || searchMode === "fbr") && (
      <>
        <TextField
          label={
            searchMode === "company"
              ? "Enter Company Invoice No"
              : "Enter FBR Invoice No"
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: { xs: "100%", md: 260 }, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff", boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)" } }}
        />
        <Button
          variant="contained"
          onClick={handleSearchClick} // <-- alert function
          startIcon={<SearchIcon />}
          sx={{
            bgcolor: "#0f172a",
            fontWeight: 900,
            borderRadius: 1.5,
            px: 2.25,
            minHeight: 42,
            boxShadow: "0 12px 24px rgba(15, 23, 42, 0.18)",
            textTransform: "none",
            "&:hover": { bgcolor: "#1e293b", boxShadow: "0 12px 24px rgba(15, 23, 42, 0.18)" }
          }}
        >
          Search
        </Button>
      </>
    )}
  </Stack>
</Paper>


      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          background: "#ffffff",
          border: "1px solid rgba(148, 163, 184, 0.22)",
          boxShadow: "0 16px 36px rgba(15, 23, 42, 0.07)",
          p: { xs: 2, md: 2.5 },
          mb: 2,
        }}
      >
       <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1.5} sx={{ flexWrap: "wrap" }}>
        <Box>
            <Typography sx={{ fontWeight: 950, color: "#0f172a", fontSize: 20 }}>
            Results
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 13 }}>
            {searchInvoices?.length || 0} invoice(s) found
          </Typography>
        </Box>
        <Chip
          label={globalData?.is_invoice_history ? "5 year history enabled" : "Last month history"}
          sx={{ bgcolor: globalData?.is_invoice_history ? "#dcfce7" : "#dbeafe", color: globalData?.is_invoice_history ? "#047857" : "#1d4ed8", fontWeight: 900, px: 1 }}
        />
       </Box>
       <Box sx={{ height: { xs: "60vh", md: "calc(100vh - 285px)" }, minHeight: 420, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
  <Table
    size="small"
    stickyHeader
    sx={{
      borderCollapse: "separate",
      borderSpacing: 0,
      minWidth: 980,
      "& th": {
        borderBottom: "1px solid #e2e8f0",
        borderRight: 0,
      },
      "& td": {
        borderBottom: "1px solid #eef2f7",
        borderRight: 0,
      },
    }}
  >
    <TableHead sx={{ "& th": { bgcolor: "#f8fafc", color: "#475569", fontWeight: 900, whiteSpace: "nowrap", py: 1.5, fontSize: 13, letterSpacing: 0 } }} >
      <TableRow>
        <TableCell>Invoice Number</TableCell>
        <TableCell>Date</TableCell>
        <TableCell>Buyer</TableCell>
        <TableCell>FBR Invoice</TableCell>
        <TableCell>Items</TableCell>
        <TableCell>QR Code</TableCell>
        <TableCell align="right">Actions</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {searchInvoices?.length > 0 ? (
        searchInvoices?.map((inv) => (
          <TableRow key={inv.id} sx={{ "& td": { py: 1.25 }, "&:hover": { bgcolor: "#f8fafc" } }}>
          <TableCell sx={{
 whiteSpace: "nowrap",
    fontWeight: 800,
    color: "#0f172a",
  }}>{inv.invoice_number}</TableCell>

             <TableCell sx={{
 whiteSpace: "nowrap",
    color: "#4b5563",
  }}>{inv.invoice_date}</TableCell>
            <TableCell sx={{
 whiteSpace: "normal",     
    wordBreak: "break-word",
    color: "#334155",
  }}>{inv.buyer_business_name}</TableCell>
            <TableCell sx={{
 whiteSpace: "nowrap",
  }}  
 >{inv.fbr_invoice_number ? (
    <Chip size="small" label={inv.fbr_invoice_number} sx={{ bgcolor: "#dcfce7", color: "#047857", fontWeight: 900 }} />
  ) : "-"}</TableCell>
            <TableCell sx={{
 whiteSpace: "nowrap",
    color: "#4b5563",
  }}>{(inv.items || []).length}</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {inv.fbr_qrcode ? (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
                    inv.fbr_qrcode
                  )}&size=64x64`}
                  alt="QR"
                  style={{ width: 42, height: 42, borderRadius: 8, border: "1px solid #e5e7eb", padding: 3, background: "#fff" }}
                />
              ) : (
                "-"
              )}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
              <Box sx={{ display: "inline-flex", justifyContent: "flex-end", gap: 0.75 }}>
              <IconButton onClick={() => setViewInvoice(inv)} color="primary" size="small" sx={{ border: "1px solid #bfdbfe", borderRadius: 1.5, bgcolor: "#eff6ff", width: 34, height: 34 }}>
                <VisibilityIcon />
              </IconButton>
              <IconButton onClick={() => handleOpenPrintChoice(inv?.id)} color="success" size="small" sx={{ border: "1px solid #bbf7d0", borderRadius: 1.5, bgcolor: "#f0fdf4", width: 34, height: 34 }}>
                <PrintIcon />
              </IconButton>
              </Box>
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ py: 10, color: "#6b7280", borderBottom: 0 }}>
            <Box sx={{ width: 62, height: 62, borderRadius: 2, bgcolor: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center", mx: "auto", mb: 1.5 }}>
              <ReceiptLongIcon fontSize="large" />
            </Box>
            <Typography sx={{ fontWeight: 950, color: "#0f172a", fontSize: 18 }}>No history found</Typography>
            <Typography sx={{ fontSize: 14, mt: 0.5, color: "#64748b" }}>Try a different date range or search by invoice number.</Typography>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</Box>

      </Paper>

      {/* View Invoice Dialog */}
      <Dialog open={!!viewInvoice} onClose={() => setViewInvoice(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 900, color: "#111827", borderBottom: "1px solid #e5e7eb" }}>
          Invoice Details
        </DialogTitle>
        <DialogContent dividers>
          {viewInvoice && (
            <>
              {/* Invoice Meta */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.25 }}>
                {[
                  ["Invoice Number", viewInvoice.invoice_number],
                  ["Invoice Type", viewInvoice.invoice_type],
                  ["Invoice Ref No", viewInvoice.invoice_ref_no],
                  ["Date", viewInvoice.invoice_date],
                  ["FBR Invoice Number", viewInvoice.fbr_invoice_number],
                  ["Created At", dayjs(viewInvoice.created_at).format("YYYY-MM-DD HH:mm")],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ p: 1.25, border: "1px solid #eef2f7", borderRadius: 1.5, bgcolor: "#f9fafb" }}>
                    <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 800 }}>{label}</Typography>
                    <Typography sx={{ color: "#111827", fontWeight: 800, mt: 0.25 }}>{value || "-"}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 1.25, p: 1.25, border: "1px solid #eef2f7", borderRadius: 1.5, bgcolor: "#f9fafb" }}>
                <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 800 }}>QR Code</Typography>
                <Typography sx={{ color: "#111827", fontWeight: 700, mt: 0.25, overflowWrap: "anywhere" }}>{viewInvoice.fbr_qrcode || "-"}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Seller Details */}
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#111827" }}>Seller Details</Typography>
              <Typography><b>Name:</b> {viewInvoice.seller_business_name}</Typography>
              <Typography><b>NTN/CNIC:</b> {viewInvoice.seller_ntn_cnic}</Typography>
              <Typography><b>Province:</b> {viewInvoice.seller_province}</Typography>
              <Typography><b>Address:</b> {viewInvoice.seller_address}</Typography>

              <Divider sx={{ my: 2 }} />

              {/* Buyer Details */}
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#111827" }}>Buyer Details</Typography>
              <Typography><b>Name:</b> {viewInvoice.buyer_business_name}</Typography>
              <Typography><b>NTN/CNIC:</b> {viewInvoice.buyer_ntn_cnic}</Typography>
              <Typography><b>Sales Tax No:</b> {viewInvoice.buyer_sales_tax_no}</Typography>
              <Typography><b>Province:</b> {viewInvoice.buyer_province}</Typography>
              <Typography><b>Address:</b> {viewInvoice.buyer_address}</Typography>
              <Typography><b>Registration Type:</b> {viewInvoice.buyer_registration_type}</Typography>
              <Typography><b>Terms of Sale:</b> {viewInvoice.terms_of_sale}</Typography>

              <Divider sx={{ my: 2 }} />

              {/* Items */}
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#111827" }}>Items</Typography>
              <Box sx={{ overflow: "auto", border: "1px solid #eef2f7", borderRadius: 1.5, mt: 1 }}>
              <Table size="small">
                <TableHead sx={{ "& th": { bgcolor: "#f9fafb", color: "#374151", fontWeight: 900, whiteSpace: "nowrap" } }}>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>HS Code</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Value Excl. ST</TableCell>
                    <TableCell>Extra Tax</TableCell>
                    <TableCell>Further Tax</TableCell>
                    <TableCell>Discount</TableCell>
                    <TableCell>FED Payable</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewInvoice?.items.map((item, i) => (
                    <TableRow key={i} sx={{ "& td": { borderBottom: "1px solid #eef2f7" } }}>
                      <TableCell>{item.product_description}</TableCell>
                      <TableCell>{item.hs_code}</TableCell>
                      <TableCell>{item.rate}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.value_sales_excluding_st}</TableCell>
                      <TableCell>{item.extra_tax}</TableCell>
                      <TableCell>{item.further_tax}</TableCell>
                      <TableCell>{item.discount}</TableCell>
                      <TableCell>{item.fed_payable}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>

              <Typography variant="subtitle1" align="right" sx={{ mt: 2 }}>
                <b>Total:</b>{" "}
                {viewInvoice.items.reduce(
                  (sum, it) =>
                    sum + (parseFloat(it.value_sales_excluding_st || 0) * (parseFloat(it.quantity) || 1)),
                  0
                ).toFixed(2)}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setViewInvoice(null)} sx={{ textTransform: "none", fontWeight: 800 }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!printChoiceInvoiceId}
        onClose={() => setPrintChoiceInvoiceId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography sx={{ fontWeight: 950, color: "#0f172a", fontSize: 24, lineHeight: 1.15 }}>
            Print Invoice
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 14, mt: 0.75 }}>
            Select the invoice layout and paper style.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25, mt: 1 }}>
            {[
              {
                title: "Old Invoice Format",
                caption: "Print the original server invoice.",
                mode: "normal",
                variant: "outlined",
              },
              {
                title: "Current Saved Design",
                caption: "Apply the saved layout and logo settings.",
                mode: "saved",
                variant: "contained",
              },
              {
                title: "Old Format Letterhead",
                caption: "Original invoice with top letterhead space.",
                mode: "normal_letterhead",
                variant: "outlined",
              },
              {
                title: "Saved Design Letterhead",
                caption: "Saved layout with top letterhead space.",
                mode: "saved_letterhead",
                variant: "outlined",
              },
            ].map((option) => (
              <Button
                key={option.mode}
                variant={option.variant}
                onClick={() => handleFetchInvoice(printChoiceInvoiceId, option.mode)}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  textAlign: "left",
                  borderRadius: 2,
                  p: 1.5,
                  minHeight: 92,
                  textTransform: "none",
                  borderColor: option.variant === "outlined" ? "#dbeafe" : undefined,
                  bgcolor: option.variant === "contained" ? "#0f172a" : "#fff",
                  color: option.variant === "contained" ? "#fff" : "#0f172a",
                  "&:hover": {
                    bgcolor: option.variant === "contained" ? "#1e293b" : "#f8fafc",
                    borderColor: "#93c5fd",
                  },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 950, fontSize: 15, lineHeight: 1.2 }}>{option.title}</Typography>
                  <Typography sx={{ color: option.variant === "contained" ? "#cbd5e1" : "#64748b", fontSize: 12.5, mt: 0.6, lineHeight: 1.35 }}>
                    {option.caption}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            onClick={() => setPrintChoiceInvoiceId(null)}
            sx={{ textTransform: "none", fontWeight: 850, borderRadius: 1.5 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden print */}
      <div style={{ display: "none" }}>
        <InvoicePrint ref={printRef} invoice={printInvoice} />
      </div>
      <div
        id="printable-content"
        className={isLetterheadPrintMode(printMode) ? "print-letterhead" : ""}
        ref={componentRef}
        style={{
          "--letterhead-top": `${getSavedInvoiceDesign(globalData).letterheadTop || 165}px`,
          position: "absolute",
          left: 0,
          top: 0,
          // width: "100%",
          zIndex: -1, 
          visibility: "hidden", 
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

    </Box>
  );
};

export default History;
